import { useTasks } from '../TaskProvider'
import { uploadPicture } from '../imageTransfer'
import { BiblePicture } from './BiblePicture'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRefreshGuard } from '../refreshGuards'
import { ImagePlus, Link2, Trash2, X } from 'lucide-react'
import {
  applyReference,
  bibleSections,
  newId,
  referenceTargets,
  removeBibleTarget,
  renameReference,
  safeReferenceUrl,
  type ReferencePick,
} from '../connectedBible'
import type {
  BibleEntry,
  BibleImage,
  BibleReference,
  StoryCharacter,
  StoryProject,
} from '../types'
import { BibleConnections } from './BibleConnections'
import { BibleSceneUses } from './BibleSceneUses'
import { ReferenceTextarea } from './ReferenceTextarea'
import { ReferenceNameInput } from './ReferenceNameInput'
import { WorkspaceDialog } from './WorkspaceDialog'
import { WritingTaskControl } from './WritingTaskControl'
import { BibleAudioSection } from './BibleAudioSection'
import { projectAudio } from '../audioTransfer'
import { openBiblePicture } from './BibleImageViewer'

export type EntryEditorProps = {
  project: StoryProject
  targetId: string
  onChange: (p: StoryProject, key?: string) => void
  onOpen: (id: string) => void
  onRemoved?: () => void
  onSaveStory: () => Promise<boolean>
}
async function readPicture(file: File): Promise<BibleImage> {
  if (
    !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
    file.size > 12000000
  )
    throw new Error(
      'Choose a PNG, JPEG, or WebP image up to 12 MB. Bramble resizes it for your bible.',
    )
  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82)
    if (dataUrl.length > 1400000)
      throw new Error(
        'This image is still too large. Choose a smaller image.',
      )
    return { id: newId(), name: file.name.slice(0, 200), alt: '', dataUrl }
  } finally {
    bitmap.close()
  }
}
export function BibleEntryEditor({
  project,
  targetId,
  onChange,
  onOpen,
  onRemoved,
  onSaveStory,
}: EntryEditorProps) {
  const { user } = useTasks()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  useRefreshGuard(uploading || !!url.trim() || !!label.trim(), 'Finish adding your picture or reference before refreshing.')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const deletePrompt = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (confirmDelete) {
      deletePrompt.current?.scrollIntoView({ block: 'nearest' })
      deletePrompt.current?.querySelector<HTMLButtonElement>('.keep-entry')?.focus({ preventScroll: true })
    }
  }, [confirmDelete])
  const latest = useRef(project)
  latest.current = project
  const currentId = useRef(targetId)
  currentId.current = targetId
  const character = project.bible?.characters.find(
    (c) => 'character:' + c.id === targetId,
  )
  const entry = project.bible?.entries?.find(
    (e) => 'entry:' + e.id === targetId,
  )
  const item = character || entry
  if (!item)
    return (
      <p className="empty-message">This entry is no longer in the bible.</p>
    )
  const title = character?.name || entry?.title || 'Untitled entry'
  const edit = (
    changes: Partial<StoryCharacter & BibleEntry>,
    field: string,
    pick?: ReferencePick,
    base = project,
  ) => {
    const bible = base.bible!
    const next = {
      ...base,
      bible: {
        ...bible,
        ...(targetId.startsWith('character:')
          ? {
              characters: bible.characters.map((c) =>
                'character:' + c.id === targetId ? { ...c, ...changes } : c,
              ),
            }
          : {
              entries: (bible.entries || []).map((e) =>
                'entry:' + e.id === targetId ? { ...e, ...changes } : e,
              ),
            }),
      },
    }
    onChange(applyReference(next, targetId, pick), targetId + ':' + field)
  }
  return (
    <article className="bible-entry-editor">
      <div className="entry-title-row">
        <span className="eyebrow">
          {character
            ? 'CHARACTER PROFILE'
            : bibleSections(project)
                .find((s) => s.id === entry?.sectionId)
                ?.title.toUpperCase()}
        </span>
        <button
          type="button"
          className="icon-button danger-action"
          aria-label="Delete bible entry"
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 size={15} />
        </button>
      </div>
      {confirmDelete && (
        <div ref={deletePrompt} className="entry-delete-confirm" role="alert">
          <p>
            Delete “{title}” and its connections? Written dialogue is kept.
            You can undo this in the editor.
          </p>
          <button
            type="button"
            className="button danger-action"
            onClick={() => {
              onChange(removeBibleTarget(project, targetId), 'delete-entry')
              onRemoved?.()
            }}
          >
            Delete entry
          </button>
          <button
            type="button"
            className="button keep-entry"
            onClick={() => setConfirmDelete(false)}
          >
            Keep entry
          </button>
        </div>
      )}
      <label className="entry-name">
        {character ? 'Character name' : 'Entry name'}
        <ReferenceNameInput
          data-live-path={JSON.stringify(character ? ['bible', 'characters', character.id, 'name'] : ['bible', 'entries', entry!.id, 'title'])}
          value={character ? character.name : entry!.title}
          maxLength={200}
          placeholder={
            character ? 'Who is this?' : 'Give this place or idea a name'
          }
          onRename={(value) =>
            onChange(
              renameReference(project, targetId, value),
              targetId + ':name',
            )
          }
        />
      </label>
      <WritingTaskControl project={project} targetId={targetId} onSaveStory={onSaveStory} />
      {character ? (
        <>
          <label>
            Role in the story
            <input
              data-live-path={JSON.stringify(['bible', 'characters', character.id, 'role'])}
              value={character.role}
              onChange={(e) => edit({ role: e.target.value }, 'role')}
              placeholder="Protagonist, neighbour, reluctant ally…"
            />
          </label>
          <label>
            Motivation & background
            <ReferenceTextarea
              aria-label="Motivation & background"
              rows={6}
              data-live-path={JSON.stringify(['bible', 'characters', character.id, 'description'])}
              value={character.description}
              project={project}
              sourceId={targetId}
              onOpenReference={onOpen}
              onText={(description, pick) =>
                edit({ description }, 'description', pick)
              }
              placeholder="What do they want? Who and what matters to them? Type @ to link an entry."
            />
          </label>
          <label>
            Voice & mannerisms
            <ReferenceTextarea
              aria-label="Voice & mannerisms"
              rows={4}
              data-live-path={JSON.stringify(['bible', 'characters', character.id, 'voice'])}
              value={character.voice}
              project={project}
              sourceId={targetId}
              onOpenReference={onOpen}
              onText={(voice, pick) => edit({ voice }, 'voice', pick)}
              placeholder="How do they sound? What would they never say?"
            />
          </label>
        </>
      ) : (
        <>
          <label>
            Section
            <select
              value={entry!.sectionId}
              onChange={(e) => edit({ sectionId: e.target.value }, 'section')}
            >
              {bibleSections(project).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            At a glance
            <input
              data-live-path={JSON.stringify(['bible', 'entries', entry!.id, 'summary'])}
              value={entry!.summary}
              onChange={(e) => edit({ summary: e.target.value }, 'summary')}
              placeholder="A short description to find this entry later"
            />
          </label>
          <label>
            Description & notes
            <ReferenceTextarea
              aria-label="Description & notes"
              rows={10}
              data-live-path={JSON.stringify(['bible', 'entries', entry!.id, 'body'])}
              value={entry!.body}
              project={project}
              sourceId={targetId}
              onOpenReference={onOpen}
              onText={(body, pick) => edit({ body }, 'body', pick)}
              placeholder="The rooms, the atmosphere, the history. Type @ to connect another entry."
            />
          </label>
        </>
      )}
      <BibleSceneUses project={project} targetId={targetId} onOpen={onOpen} />
      <section className="entry-images">
        <h3>Pictures & visual references</h3>
        <p>
          A face, a floor plan, or the feeling of a place. Images travel with
          your project backup.
        </p>
        <div className="entry-image-grid">
          {(item.images || []).map((picture) => (
            <figure key={picture.id}>
              <button type="button" className="bible-picture-open" aria-label={'View picture: ' + (picture.alt || picture.name)} onClick={() => openBiblePicture(targetId, picture.id)}>
                <BiblePicture picture={picture} alt={picture.alt || picture.name} />
              </button>
              <figcaption>
                <input
                  aria-label={'Caption for ' + picture.name}
                  value={picture.alt}
                  maxLength={1000}
                  placeholder="Add a caption or image description"
                  onChange={(e) =>
                    edit(
                      {
                        images: item.images!.map((i) =>
                          i.id === picture.id
                            ? { ...i, alt: e.target.value }
                            : i,
                        ),
                      },
                      'caption',
                    )
                  }
                />
                <button
                  type="button"
                  className="icon-button"
                  aria-label={'Remove image ' + picture.name}
                  onClick={() =>
                    edit(
                      {
                        images: item.images!.filter(
                          (i) => i.id !== picture.id,
                        ),
                      },
                      'remove-image',
                    )
                  }
                >
                  <X size={14} />
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
        <label
          className={'button image-upload' + (uploading ? ' is-busy' : '')}
        >
          <ImagePlus size={15} />
          {uploading ? 'Preparing image…' : 'Add picture'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={uploading || (item.images?.length || 0) >= 12}
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file) return
              setUploading(true)
              setError('')
              const uploadingTo = targetId
              try {
                const image = await uploadPicture(await readPicture(file), user.id)
                if (!mounted.current || currentId.current !== uploadingTo)
                  return
                const latestItem =
                  latest.current.bible?.characters.find(
                    (c) => 'character:' + c.id === targetId,
                  ) ||
                  latest.current.bible?.entries?.find(
                    (c) => 'entry:' + c.id === targetId,
                  )
                if (latestItem)
                  edit(
                    { images: [...(latestItem.images || []), image] },
                    'add-image',
                    undefined,
                    latest.current,
                  )
              } catch (error) {
                setError(
                  error instanceof Error
                    ? error.message
                    : 'This image could not be opened.',
                )
              } finally {
                setUploading(false)
              }
            }}
          />
        </label>
        <small>
          PNG, JPEG, WebP · resized to 1,200 px · up to 12 per entry
        </small>
      </section>
      <BibleAudioSection key={targetId} audio={item.audio || []} allAudio={projectAudio(project)} path={character ? ['bible', 'characters', character.id] : ['bible', 'entries', entry!.id]}
        onChange={(audio, field) => edit({ audio }, field, undefined, latest.current)} />
      <section className="entry-references">
        <h3>Reference links</h3>
        {(item.references || []).map((reference) => (
          <div className="external-reference" key={reference.id}>
            <a
              href={safeReferenceUrl(reference.url) || undefined}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Link2 size={14} />
              {reference.label || reference.url}
            </a>
            <button
              type="button"
              className="icon-button"
              aria-label={'Remove reference ' + reference.label}
              onClick={() =>
                edit(
                  {
                    references: item.references!.filter(
                      (r) => r.id !== reference.id,
                    ),
                  },
                  'remove-reference',
                )
              }
            >
              <X size={13} />
            </button>
          </div>
        ))}
        <form
          className="reference-form"
          onSubmit={(e) => {
            e.preventDefault()
            const safe = safeReferenceUrl(url)
            if (!safe) {
              setError('Use a complete http or https link.')
              return
            }
            const reference: BibleReference = {
              id: newId(),
              label: label.trim() || new URL(safe).hostname,
              url: safe,
            }
            edit(
              { references: [...(item.references || []), reference] },
              'add-reference',
            )
            setLabel('')
            setUrl('')
            setError('')
          }}
        >
          <label>
            Label
            <input
              value={label}
              maxLength={200}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Research, inspiration…"
            />
          </label>
          <label>
            Web address
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              placeholder="https://…"
            />
          </label>
          <button type="submit" className="button" disabled={!url.trim()}>
            Add link
          </button>
        </form>
      </section>
      <BibleConnections
        key={'connections:' + targetId}
        project={project}
        sourceId={targetId}
        onChange={onChange}
        onOpen={onOpen}
      />
      {error && (
        <p role="alert" className="room-error">
          {error}
        </p>
      )}

    </article>
  )
}
export function BiblePeek({
  onClose,
  ...props
}: EntryEditorProps & { onClose: () => void }) {
  const target = referenceTargets(props.project).find(
    (t) => t.id === props.targetId,
  )
  return (
    <WorkspaceDialog
      title={target?.title || 'Bible entry'}
      eyebrow="REFERENCE DESK · YOUR DRAFT STAYS OPEN"
      onClose={onClose}
      className="bible-peek"
    >
      <div className="bible-peek-scroll"><BibleEntryEditor key={props.targetId} {...props} onRemoved={onClose} /></div>
    </WorkspaceDialog>
  )
}
